import type { CSSProperties } from 'react'
import type { Article } from './types'

import { convertMonthEnglishFormat } from '@/utils'
import { cmsImageSrc } from '@/utils/cmsImageSrc'
import { ArticleTagPill } from './ArticleTagPill'
import { truncateSubtitleHtml, truncateTitle } from './truncate'

/**
 * Category badge overlaid on the card image. The two call sites that show it
 * use *different* inline styles, so the variant is explicit rather than unified
 * — collapsing them would silently restyle the article-detail rail.
 */
export type CategoryBadgeVariant =
  | 'none'
  /** Guides listing: responsive font size + single-line ellipsis. */
  | 'responsive'
  /** Article detail rail: no font sizing, no ellipsis handling. */
  | 'compact'

const BADGE_BASE: CSSProperties = {
  position: 'absolute',
  backgroundColor: 'var(--alrajhi-brand800)',
  color: '#fff',
  padding: '12px',
  borderRadius: '0 0 16px 0',
  lineHeight: '12px'
}

function badgeStyle(variant: CategoryBadgeVariant, isTabScreen: boolean): CSSProperties {
  if (variant === 'compact') return BADGE_BASE
  return {
    ...BADGE_BASE,
    fontSize: isTabScreen ? '12px' : '14px',
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}

export function ArticleCard({
  item,
  isMobile,
  isTabScreen,
  onClick,
  categoryBadge = 'none',
  imageFallbackSrc,
  imageAltFallback
}: {
  item: Article
  isMobile: boolean
  isTabScreen: boolean
  onClick: () => void
  categoryBadge?: CategoryBadgeVariant
  /** Used when the CMS image is missing. Guides' listing intentionally has none. */
  imageFallbackSrc?: string
  imageAltFallback?: string
}) {
  const imageSrc = imageFallbackSrc
    ? cmsImageSrc(item?.image?.imageLink) || imageFallbackSrc
    : cmsImageSrc(item?.image?.imageLink)

  return (
    <div className="guide-latest-article" onClick={onClick}>
      <div className="guide-latest-article-top-box">
        <div className="guide-LA-image-container">
          {categoryBadge !== 'none' && (
            <span style={badgeStyle(categoryBadge, isTabScreen)}>
              {item?.guide_category?.title}
            </span>
          )}
          <img
            alt={item?.image?.altText || imageAltFallback}
            src={imageSrc}
            style={{ display: 'block', width: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="guide-LA-content-container content">
          <p className="fw-bold">{convertMonthEnglishFormat(item.date)}</p>
          {item.title && <h4>{truncateTitle(item.title, isMobile, isTabScreen)}</h4>}
          {item.subtitle && (
            /* <p> (not div): truncateSubtitleHtml returns a plain string here, and
               the leaked `.content p` typography from the old bundle keys on the tag */
            <p>{truncateSubtitleHtml(item.subtitle, isMobile, isTabScreen)}</p>
          )}
        </div>
      </div>

      <div className="d-none d-sm-block">
        <div className="guide-tags-container content">
          {item?.article_tags?.map(tag => (
            <ArticleTagPill key={tag.title}>{tag.title}</ArticleTagPill>
          ))}
        </div>
      </div>
    </div>
  )
}
